// I'm not sure about adding underlying types too

import { ColDef, ColGroupDef, GridOptions } from "ag-grid-community";
import * as _ from "lodash-es";

type AGGrid_ColDef = ColDef;
export type ColDefOrGroup = ColDef|ColGroupDef

export interface ObjDisplayerA {
    displayer: "obj";
    max_length?: number;
}
export interface BooleanDisplayerA {
    displayer: "boolean";
}
export interface StringDisplayerA {
    displayer: "string";
    max_length?: number;
    // When set, wrap matches in a <mark> with the configured background.
    // Matching is case-insensitive; an array highlights any of the phrases.
    // Mutually exclusive with highlight_regex — regex wins if both are supplied.
    highlight_phrase?: string | string[];
    highlight_regex?: string; // JS regex source (no slashes/flags); applied case-insensitively
    highlight_color?: string; // any CSS color; defaults to "yellow"
}
export interface FloatDisplayerA {
    displayer: "float";
    min_fraction_digits: number;
    max_fraction_digits: number;
}

export interface DatetimeDefaultDisplayerA {
    displayer: "datetimeDefault";
}
export interface IntegerDisplayerA {
    displayer: "integer";
    min_digits: number;
    max_digits: number;
}

export interface CompactNumberDisplayerA {
    displayer: "compact_number";
}

export interface DurationDisplayerA {
    displayer: "duration";
}

export interface InheritDisplayerA {
    displayer: "inherit";
}

export interface DatetimeLocaleDisplayerA {
    displayer: "datetimeLocaleString";
    locale: "en-US" | "en-GB" | "en-CA" | "fr-FR" | "es-ES" | "de-DE" | "ja-JP";
    args: Intl.DateTimeFormatOptions;
}

// Used DisplayerA instead of FormatterArgs,  Displayer makes sense from the python side
// python doesn't care that histogram requires a cellRenderer and Integer only changes the formatter
export type FormatterArgs =
    | ObjDisplayerA
    | BooleanDisplayerA
    | StringDisplayerA
    | FloatDisplayerA
    | DatetimeDefaultDisplayerA
    | DatetimeLocaleDisplayerA
    | IntegerDisplayerA
    | CompactNumberDisplayerA
    | DurationDisplayerA;

export interface HistogramDisplayerA {
    displayer: "histogram";
}

export interface ChartDisplayerA {
    displayer: "chart";
    colors?: {
        custom1_color:string;
        custom2_color:string;
        custom3_color:string;
    }
}

export interface LinkifyDisplayerA {
    displayer: "linkify";
}
export interface BooleanCheckboxDisplayerA {
    displayer: "boolean_checkbox";
}

export interface Base64PNGImageDisplayerA {
    displayer: "Base64PNGImageDisplayer";
}

export interface SVGDisplayerA {
    displayer: "SVGDisplayer";
}

export type CellRendererArgs =
    | HistogramDisplayerA
    | ChartDisplayerA
    | LinkifyDisplayerA
    | BooleanCheckboxDisplayerA
    | Base64PNGImageDisplayerA
    | SVGDisplayerA;

export type DisplayerArgs = FormatterArgs | CellRendererArgs | InheritDisplayerA;

export const cellRendererDisplayers = [
    "chart",
    "histogram",
    "linkify",
    "Base64PNGImageDisplayer",
    "SVGDisplayer",
];

export type ColorMap = "BLUE_TO_YELLOW" | "DIVERGING_RED_WHITE_BLUE" | "DIVERGING_BLUE_WHITE_RED" | string[];

//ColorMapRules
export interface ColorMapRules {
    color_rule: "color_map";
    map_name: ColorMap;
    //optional, the column to base the ranges on.  the proper histogram_bins must still be sent in for that column
    val_column?: string;
}

//ColorCategorical rules
export interface ColorCategoricalRules {
    color_rule: "color_categorical";
    map_name: ColorMap;
    //optional, the column to base the ranges on.  the proper histogram_bins must still be sent in for that column
    val_column?: string;
}

//if exist_column is not null,  set cell style to condtional_color... used for highlighting changed values or errored_rows
export interface ColorWhenNotNullRules {
    color_rule: "color_not_null";
    conditional_color: string | "red";
    exist_column: string;
}

export interface ColorFromColumn {
    color_rule: "color_from_column";
    val_column: string;
}

// Apply a single constant background color to every cell in the column.
// Useful for visually marking a column's role (e.g. join key, sentinel) without
// depending on any data value.
export interface ColorStaticRules {
    color_rule: "color_static";
    color: string;
}

export type ColorMappingConfig = ColorMapRules | ColorWhenNotNullRules | ColorFromColumn | ColorCategoricalRules | ColorStaticRules;

//TooltipRules
export interface SimpleTooltip {
    tooltip_type: "simple";
    val_column: string;
}

export interface SummarySeriesTooltip {
    tooltip_type: "summary_series";
}

export type TooltipConfig = SimpleTooltip | SummarySeriesTooltip; //more to be added

export type BaseColumnConfig = {
  displayer_args: DisplayerArgs;
  color_map_config?: ColorMappingConfig;
  tooltip_config?: TooltipConfig;
  ag_grid_specs?: AGGrid_ColDef;
};
export type NormalColumnConfig = BaseColumnConfig & {
  col_name:string;
  header_name:string;
}
export type MultiIndexColumnConfig = BaseColumnConfig & {
  col_path:string[];
  field:string;
}

export type ColumnConfig = NormalColumnConfig | MultiIndexColumnConfig;


export type PinnedRowConfig = {
    // A leading `?` marks an optional row: the unprefixed key is looked up
    // in the summary stats, and if absent the row is omitted entirely
    // (rather than rendered with undefined values). Used for scoped stats
    // like `?cleaned_histogram_bins` / `?filtered_histogram_bins` that
    // only exist when their scope is active.
    primary_key_val: string;
    displayer_args: DisplayerArgs;
    //used to render index column values with string not the specified displayer, otherwise the column will be listed as NaN or blank
    //by default the "index" column is always rendered with "obj"
    default_renderer_columns?: string[];
};

export type ThemeColorConfig = {
    accentColor?: string;
    accentHoverColor?: string;
    backgroundColor?: string;
    foregroundColor?: string;
    oddRowBackgroundColor?: string;
    borderColor?: string;
    headerBorderColor?: string;
    headerBackgroundColor?: string;
    spacing?: number;
    cellHorizontalPaddingScale?: number;
    rowVerticalPaddingScale?: number;
};

export type ThemeConfig = ThemeColorConfig & {
    colorScheme?: 'light' | 'dark' | 'auto';
    light?: ThemeColorConfig;
    dark?: ThemeColorConfig;
};

export type ComponentConfig = {
    height_fraction?: number;
    // temporary debugging props
    dfvHeight?: number;
    // "autoHeight": grow to fit all rows (no scroll). "normal": fixed dfvHeight
    // viewport with scroll. "fitContent": auto-detect between those AND size the
    // widget's own outer box to its content, so the host container doesn't need
    // to match the viewport height (removes the gap below short tables).
    layoutType?: "autoHeight" | "normal" | "fitContent";
    shortMode?: boolean;
    selectionBackground?: string;
    className?: string;
    theme?: ThemeConfig;
};

export interface DFViewerConfig {
    pinned_rows: PinnedRowConfig[];
    column_config: ColumnConfig[];
    left_col_configs:  ColumnConfig[];
    extra_grid_config?: GridOptions;
    component_config?: ComponentConfig;
}

export type DFDataRow = Record<
    string,
    string | number | boolean | any[] | Record<string, any> | null
>;

export type DFData = DFDataRow[];

// Transport envelope — a tagged discriminated union on `format`. The single
// JS decoder `decodeDFData` consumes these; the single Python `encode_df`
// produces them. Bytes ride inline (`parquet_b64`/`json`) or in a comm
// side-channel buffer (`parquet_buffer`, addressed by `buffer_index`).
//
// `layout` is orthogonal to transport: 'wide' marks the single-row
// {col}__{stat} summary-stats shape that must be pivoted after decode.
//
// `json_columns` names exactly the columns whose string cells are JSON-encoded
// (the fastparquet `object_encoding='json'` convention). The decoder JSON-parses
// only those columns and passes every other cell through. Mirrors fastparquet's
// per-column model. When absent, the decoder falls back to legacy parse-all
// (correct for the pandas path, where every object column is JSON-wrapped).
// Native-parquet senders (polars/xorq/pyarrow) emit `json_columns: []`.
//
// Names here are in *rewritten* column space (the internal a, b, c... names the
// pipeline assigns), not the original column names — the decoder matches them
// against the parquet row keys, which are already rewritten. A non-empty list
// using original names would match nothing and leave JSON-encoded cells raw.
export type DFEnvelope =
    | { format: 'parquet_buffer'; buffer_index: number; layout?: 'wide' | 'row'; json_columns?: string[] } // bytes in buffers[]
    | { format: 'parquet_b64'; data: string; layout?: 'wide' | 'row'; json_columns?: string[] }            // base64 parquet, inline
    | { format: 'json'; data: DFData; layout?: 'wide' | 'row' };                                            // record array, inline

// A value in df_data_dict / a wire payload can be plain JSON (DFData) or a
// transport envelope. DFData passthrough is retained.
export type DFDataOrPayload = DFData | DFEnvelope;

/*
When I want to start tagging metadata onto DFData
export interface DFData {
  rows: DFDataRow[];
  //data_meta expansion point for typing info about the data as needed by non-display stuff
  // typing,  sorting, null handling I'm not sure about it
};
*/

export interface DFWhole {
    dfviewer_config: DFViewerConfig;
    data: DFData;
}

export const EmptyDf: DFWhole = {
    dfviewer_config: {
        pinned_rows: [],
        column_config: [],
      left_col_configs: []
    },
    data: [],
};

//actually SDFT is summary stats transposed to be useful
// SDFT[col][stat_name]
// SDFT really only needs histogramBins and histogramLogBins and...? at this point
//type SDFT = any;

export interface SDFMeasure {
    histogram_bins: number[];
    histogram_log_bins: number[];
}

export type SDFT = Record<string, SDFMeasure>;
